import React, { useEffect, useState } from 'react';
import { User, UserRole } from '../types';

interface Props {
  user: User;
  /** Appelé quand l'utilisateur clique sur "Activer maintenant". L'app navigue vers Paramètres > Sécurité. */
  onNavigateToSecurity: () => void;
}

const DISMISS_KEY = 'orion_2fa_banner_dismissed_until';
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000; // 24h — la bannière revient le lendemain

/**
 * Bannière de recommandation affichée aux comptes admin sans 2FA active.
 * Non bloquante, dismissible 24h. Cohérente avec la politique "le 2FA reste
 * un choix" tout en gardant une pression douce sur les comptes à fort impact.
 *
 * Palette amber translucide → s'intègre aussi bien au mode clair qu'au mode sombre
 * sans dépendre du thème actif.
 */
const AdminTwoFactorBanner: React.FC<Props> = ({ user, onNavigateToSecurity }) => {
  const [hidden, setHidden] = useState(true);

  const isAdmin = user.role === UserRole.COMPANY_ADMIN || user.role === UserRole.SUPER_ADMIN;

  useEffect(() => {
    if (!isAdmin || user.totpEnabled) {
      setHidden(true);
      return;
    }
    const until = Number(localStorage.getItem(DISMISS_KEY) || '0');
    setHidden(Date.now() < until);
  }, [isAdmin, user.totpEnabled]);

  if (!isAdmin || user.totpEnabled || hidden) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DURATION_MS));
    setHidden(true);
  };

  return (
    <div className="orion-warn-soft border-x-0 border-t-0 px-4 py-2.5 flex items-center justify-between gap-4 flex-wrap rounded-none">
      <div className="flex items-center gap-3 flex-1 min-w-[280px]">
        <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <p className="text-sm font-medium">
          <strong className="font-bold">Votre compte administrateur n'est pas protégé par la 2FA.</strong>{' '}
          Activez-la pour réduire fortement le risque de compromission, même en cas de fuite de mot de passe.
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={onNavigateToSecurity}
          className="orion-warn-btn-primary px-4 py-1.5 rounded-lg text-xs transition-colors"
        >
          Activer maintenant
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="orion-warn-btn-ghost px-3 py-1.5 rounded-lg text-xs transition-colors"
          title="Masquer pour 24h"
        >
          Plus tard
        </button>
      </div>
    </div>
  );
};

export default AdminTwoFactorBanner;
